import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { api, media, img } from "../lib/api";

const CATS = [
  { id: "all", label: "All" },
  { id: "st_louis", label: "St. Louis" },
  { id: "community", label: "Community" },
  { id: "studio", label: "Studio" },
];

export default function PhotoGallery() {
  const [photos, setPhotos] = useState([]);
  const [cat, setCat] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (cat === "all" ? photos : photos.filter((p) => p.category === cat)),
    [photos, cat]
  );

  return (
    <section id="gallery" className="relative py-24 bg-[#0C1424]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#0066FF] mb-3">Our City. Our Stories.</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">Photo Gallery</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`gallery-filter-${c.id}`}
                className="font-cond font-semibold uppercase text-sm px-4 py-2 rounded-full border transition-colors"
                style={
                  cat === c.id
                    ? { background: "#0066FF", color: "#fff", borderColor: "#0066FF" }
                    : { background: "transparent", color: "#94A3B8", borderColor: "rgba(0,102,255,0.25)" }
                }
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="columns-2 lg:columns-3 gap-4 space-y-4" data-testid="photo-gallery-grid">
          {filtered.map((p, i) => (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
              onClick={() => setActive(p)}
              data-testid={`photo-item-${p.id}`}
              className="group relative w-full break-inside-avoid rounded-xl overflow-hidden border border-[#0066FF]/15 hover:border-[#0066FF]/50 transition-colors block"
            >
              <img
                src={img(p.image_file_id, p.image_url)}
                alt={p.title}
                className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="font-cond font-bold uppercase text-white">{p.title}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="photo-lightbox-modal"
        >
          <button
            onClick={() => setActive(null)}
            data-testid="photo-lightbox-close"
            className="absolute top-6 right-6 text-white hover:text-[#FF6B00]"
          >
            <X className="h-8 w-8" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl">
            <img
              src={media(active.image_file_id, active.image_url)}
              alt={active.title}
              className="max-h-[80vh] w-auto rounded-xl border border-[#0066FF]/30"
            />
            <div className="mt-3 font-cond font-bold uppercase text-xl text-center">{active.title}</div>
          </div>
        </div>
      )}
    </section>
  );
}
